import { Component, OnInit, effect, inject, model, untracked, viewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../base/base.component';
import { FileTypes } from '../../constants';
import {
  AttachedDocuments,
  ConfiguredAttachment,
  DragAndDropDocument,
  EmailContact,
  FileData,
  ReportTemplate,
} from '../../models';
import {
  ConfiguredAttachmentService,
  DragAndDropService,
  FileService,
  FormatService,
  ModalService,
  NotificationService,
} from '../../services';
import { FormlyService } from '../../services/component-services/formly.service';
import { FormlyRecipientsComponent } from '../formly/formly-recipients/formly-recipients.component';
import { SummaryTopComponent } from '../summary-top/summary-top.component';
import { BaseFormComponent } from '../base-form/base-form.component';
import { EmailContactPickerModalComponent } from './email-contact-picker-modal/email-contact-picker-modal.component';

/** One chip on the attachment strip, whichever of the three sources it came from. */
interface AttachmentChip {
  key: string;
  name: string;
  extension: string;
  sizeBytes?: number;
  /** Why it is attached: "Document", "Always", a country name — or nothing for a per-send file. */
  tag?: string;
  removable: boolean;
  openable: boolean;
  fullPath?: string;
}

/**
 * The shared report email compose window (every Reports tab's Email button) — reads like a
 * mail client: To/Cc/Bcc recipient chips, subject, ONE attachment strip, a formatted body,
 * and Send/Cancel in the footer.
 *
 * Closes (Send) with the ReportTemplate VALUE — recipients as comma-separated strings, the
 * body in the narrative storage contract (the rich-text field writes the marker itself).
 * The caller (report-toolbar) does the sending; this modal only composes.
 */
@Component({
  selector: 'app-email-modal',
  templateUrl: './email-modal.component.html',
  styleUrl: './email-modal.component.scss',
  imports: [SummaryTopComponent, BaseFormComponent],
})
export class EmailModalComponent extends BaseComponent implements OnInit {
  private readonly configuredAttachmentService = inject(ConfiguredAttachmentService);
  private readonly dragAndDropService = inject(DragAndDropService);
  private readonly fileService = inject(FileService);
  // Protected: the template shows each chip's size via formatService.fileSize.
  protected readonly formatService = inject(FormatService);
  private readonly formlyService = inject(FormlyService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);

  private readonly headerForm = viewChild<BaseFormComponent>('headerForm');
  private readonly bodyForm = viewChild<BaseFormComponent>('bodyForm');

  public readonly reportTemplate = model<ReportTemplate>({
    ccEmail: '',
    email: '',
    emailBody: '',
    emailContacts: [],
    emailSubject: '',
    report: '',
    sellingCompany: '',
    suffix: '',
    template: undefined,
  });

  /**
   * The Document being sent and the record it is for. Both optional: a caller that does not set
   * them simply gets no configured-attachment list, which is how this screen behaved before they
   * existed.
   */
  public readonly documentType = model<string>();
  public readonly reportId = model<string>();

  /**
   * The report's own PDF — generated by the caller before the modal opens, so the one
   * attachment that is certain to go can be SEEN (named and sized) rather than being invisible
   * until the mail arrives. Display only; the caller keeps the actual file for the send.
   */
  public readonly documentPdfName = model<string>();
  public readonly documentPdfSize = model<number>();

  protected fields: FormlyFieldConfig[];
  protected bodyFields: FormlyFieldConfig[];
  protected readonly header = $localize`Email`;
  protected readonly attachFileText = $localize`Attach file`;

  // Hoisted because stripItems()/attachmentSummary() run every change-detection pass — a
  // $localize inside them performs a translation lookup per pass, and a string with no
  // translation yet then warns to the console continuously.
  private readonly documentTag = $localize`Document`;
  private readonly alwaysTag = $localize`Always`;
  private readonly attachmentSingular = $localize`attachment`;
  private readonly attachmentPlural = $localize`attachments`;

  protected attachedDocuments: AttachedDocuments[] = [];
  protected configuredAttachments: ConfiguredAttachment[] = [];
  protected dragActive = false;

  /** Bcc stays hidden chrome until asked for, like every mail client. */
  protected showBcc = false;

  private dragAndDropDocument: DragAndDropDocument = { folderName: '', subFolderName: '', fileName: '', container: '' };
  private initialised = false;

  constructor() {
    super();

    // ng-bootstrap renders the modal synchronously inside modalService.open(), BEFORE the
    // caller can set the inputs — so anything read in ngOnInit reads the defaults. React to
    // the real template landing instead.
    effect(() => {
      const template = this.reportTemplate();
      this.documentType();
      this.reportId();
      untracked(() => this.initialise(template));
    });
  }

  ngOnInit() {
    this.getFields();
  }

  protected onSend() {
    const headerValid = this.headerForm()?.validate() ?? false;
    const bodyValid = this.bodyForm()?.validate() ?? false;
    if (!headerValid || !bodyValid) {
      return;
    }

    // The VALUE, not the signal — the caller reads .email/.emailSubject off what we close with.
    this.modalService.close({ ...this.reportTemplate() });
  }

  protected onCancel() {
    this.modalService.dismiss();
  }

  /** Send needs somewhere to go and something on the subject line; the rest is optional. */
  protected sendDisabled(): boolean {
    const template = this.reportTemplate();
    const to = FormlyRecipientsComponent.split(template?.email);
    return !to.length || !to.every(FormlyRecipientsComponent.isValidEmail) || !template?.emailSubject?.trim();
  }

  protected stripItems(): AttachmentChip[] {
    const chips: AttachmentChip[] = [];

    if (this.documentPdfName()) {
      chips.push({
        key: 'document-pdf',
        name: this.documentPdfName(),
        extension: 'pdf',
        sizeBytes: this.documentPdfSize(),
        tag: this.documentTag,
        removable: false,
        openable: false,
      });
    }

    for (const attachment of this.configuredAttachments) {
      chips.push({
        key: `configured-${attachment.id}`,
        name: attachment.fileName,
        extension: this.extension(attachment.fileName),
        sizeBytes: attachment.fileSize,
        // The tag answers "why is this one here?" — a file set up for one country appears
        // only for that country's customers; the rest go on every send.
        tag: attachment.countryId ? attachment.country : this.alwaysTag,
        removable: false,
        openable: false,
      });
    }

    for (const document of this.attachedDocuments ?? []) {
      chips.push({
        key: `added-${document.fullPath}`,
        name: document.documentName,
        extension: this.extension(document.documentName),
        sizeBytes: document.fileSize,
        removable: true,
        openable: true,
        fullPath: document.fullPath,
      });
    }

    return chips;
  }

  /** The footer's running count and total — "4 attachments · 264 KB". */
  protected attachmentSummary(): string {
    const chips = this.stripItems();
    if (!chips.length) {
      return '';
    }

    const bytes = chips.reduce((total, x) => total + (x.sizeBytes ?? 0), 0);
    const noun = chips.length === 1 ? this.attachmentSingular : this.attachmentPlural;
    return `${chips.length} ${noun} · ${this.formatService.fileSize(bytes)}`;
  }

  protected openDocument(chip: AttachmentChip) {
    if (chip.openable) {
      this.dragAndDropService.openPreview(chip.fullPath);
    }
  }

  protected removeDocument(chip: AttachmentChip) {
    this.dragAndDropService
      .removeFile(chip.fullPath)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.listFiles();
        this.notificationService.success($localize`File removed successfully`);
      });
  }

  // The whole modal body is the drop target, with an overlay while a file is over it —
  // dragenter/leave fire on every child, so a depth counter is what keeps the overlay steady.
  private dragDepth = 0;

  protected onDragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragDepth++;
    this.dragActive = true;
  }

  protected onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  protected onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    this.dragActive = this.dragDepth > 0;
  }

  protected onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragActive = false;

    for (const file of Array.from(event.dataTransfer?.files ?? [])) {
      this.readAndAttach(file);
    }
  }

  protected onFileChosen(event: Event) {
    const input = event.target as HTMLInputElement;
    for (const file of Array.from(input.files ?? [])) {
      this.readAndAttach(file);
    }
    input.value = '';
  }

  protected openContactPicker() {
    const { modal, component } = this.modalService.openMedium<EmailContactPickerModalComponent>(
      EmailContactPickerModalComponent,
    );
    component.contacts.set(this.reportTemplate()?.emailContacts ?? []);
    modal.result.then(
      (selected: EmailContact[]) => this.addContactsToCc(selected),
      () => undefined,
    );
  }

  protected revealBcc() {
    this.showBcc = true;
    // ⚠️ Formly only re-evaluates hide expressions on a MODEL change (checkExpressionOn
    // defaults to 'modelChange'), and this click changed a component flag, not the model —
    // without this nudge the Bcc row never appears. A fresh reference is change enough.
    this.reportTemplate.update((template) => ({ ...template }));
  }

  private addContactsToCc(selected: EmailContact[]) {
    if (!selected?.length) {
      return;
    }

    const existing = FormlyRecipientsComponent.split(this.reportTemplate().ccEmail);
    const added = selected
      .map((x) => x.email)
      .filter((email) => email && !existing.some((x) => x.toLowerCase() === email.toLowerCase()));

    // A new object through the signal, so the form re-reads the model — mutating the current
    // one would leave the Cc control showing the old value.
    this.reportTemplate.update((template) => ({
      ...template,
      ccEmail: [...existing, ...added].join(','),
    }));
  }

  private initialise(template: ReportTemplate) {
    // The effect fires once with the defaults before the caller's set() lands; the real
    // template is the one with a subject — and it must run ONCE. The model gets a fresh
    // reference on every in-modal update (revealing Bcc, adding contacts), and re-initialising
    // then would stomp showBcc back to hidden and re-fetch the attachment lists. (Exactly
    // that ate the first click on the Bcc link, 2026-08-26.)
    if (!template?.emailSubject || this.initialised) {
      return;
    }

    this.initialised = true;
    this.showBcc = !!template.bccEmail;
    this.dragAndDropDocument.folderName = template.emailSubject;
    this.dragAndDropDocument.subFolderName = $localize`Attachments`;
    this.listFiles();
    this.getConfiguredAttachments();
  }

  private getConfiguredAttachments() {
    this.configuredAttachmentService
      .forEmail(this.documentType(), this.reportId())
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: ConfiguredAttachment[]) => (this.configuredAttachments = model));
  }

  private getFields() {
    // wideField on EVERY field, so the whole compose column lines up — the ones without it
    // would keep the 5/7 label split and sit misaligned under the ones with it (ui.md).
    this.fields = [
      this.formlyService.recipients('email', $localize`To`, {
        required: true,
        wideField: true,
        recipientName: (email: string) => this.contactName(email),
      }),
      this.formlyService.recipients('ccEmail', $localize`Cc`, {
        wideField: true,
        recipientName: (email: string) => this.contactName(email),
        actions: [
          { text: $localize`Bcc`, onClick: () => this.revealBcc() },
          { text: $localize`Contacts…`, onClick: () => this.openContactPicker() },
        ],
      }),
      this.formlyService.recipients(
        'bccEmail',
        $localize`Bcc`,
        { wideField: true, recipientName: (email: string) => this.contactName(email) },
        () => !this.showBcc,
      ),
      this.formlyService.text('emailSubject', $localize`Subject`, { required: true, wideField: true }),
    ];

    // Its own form below the attachment strip. links: true is the one place the rich-text
    // editor may offer hyperlinks — the API sanitises against the email allowlist, not the
    // narrative one (see EmailBodyConstants).
    this.bodyFields = [
      this.formlyService.richText('emailBody', $localize`Body`, { links: true, tall: true, wideField: true }),
    ];
  }

  /** The chip for an address the contact list knows shows the contact's name. */
  private contactName(email: string): string {
    const contact = (this.reportTemplate()?.emailContacts ?? []).find(
      (x: EmailContact) => x.email?.toLowerCase() === email.toLowerCase(),
    );
    return contact ? `${contact.forename ?? ''} ${contact.surname ?? ''}`.trim() || email : email;
  }

  private extension(fileName: string): string {
    const dot = fileName?.lastIndexOf('.') ?? -1;
    return dot > -1 ? fileName.slice(dot + 1).toLowerCase() : '';
  }

  private readAndAttach(file: File) {
    const reader = new FileReader();
    reader.onload = () => this.attachFiles({ name: file.name, src: reader.result } as FileData);
    reader.readAsDataURL(file);
  }

  attachFiles(file: FileData) {
    this.dragAndDropDocument.fileName = file.name;

    const fileType = this.extension(file.name);
    const upload =
      fileType === FileTypes.jpeg ||
      fileType === FileTypes.jpg ||
      fileType === FileTypes.png ||
      fileType === FileTypes.bmp
        ? this.dragAndDropService.uploadImage(this.dragAndDropDocument, this.fileService.imageToFormData(file.src))
        : this.dragAndDropService.uploadFile(this.dragAndDropDocument, this.fileService.fileToFormData(file.src));

    // Refresh and report AFTER the upload lands — the old screen listed and toasted before
    // the request had gone anywhere.
    upload.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.listFiles();
        this.notificationService.success($localize`File attached successfully`);
      },
      error: () => this.notificationService.fail($localize`File failed to attach`),
    });
  }

  private listFiles() {
    this.dragAndDropService
      .listFiles(this.dragAndDropDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: AttachedDocuments[]) => (this.attachedDocuments = model));
  }
}
